import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  IconButton,
  Image,
  Link,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useColorMode,
  VStack,
  Wrap,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { BsArrowLeft, BsFillPatchCheckFill, BsFlag } from 'react-icons/bs'
import { truncate } from '../../utils/stringUtils'
import {
  useOnChainRaffle,
  useOnChainRaffleParticipants,
  useOnChainRaffleUserData,
  useRaffleBySlug,
} from '../techRaffles/hooks/raffle'
import RaffleCountdown from './components/RaffleCountdown'
import { useWallet } from '@solana/wallet-adapter-react'
import { useTokens, useTokensQuick } from '../techRaffles/hooks/token'
import { LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js'
import { getBaseUrl } from '../../config/config'
import { SaveRaffleButton } from './components/SaveRaffleButton'
import { ShareModal } from './components/ShareModal'
import { navigateBack } from '../../utils/navigation'
import { raffleTypeWithLikeCount, raffleMinType, raffleType } from '../techRaffles/types'
import { hasRaffleEnded } from '../techRaffles/raffleUtils'
import { RaffleParticipant } from './components/RaffleParticipant'
import { RaffleParticipantAvatar } from './components/RaffleParticipantAvatar'
import { BuyTickets } from './components/BuyTickets'
import { UseOnChainRaffleType } from '../techRaffles/hooks/hookTypes'
import { Won } from './components/Won'
import { ProfilePicture } from './components/ProfilePicture'
import RaffleHostInfoAndCancelBox from './components/RaffleHostInfoAndCancelBox'
import { formatFloatForDisplay } from '../../utils/utils'
import { useIsUserPlatformAdmin, useUser } from '../common/auth/authHooks'
import PlatformAdminRaffleInfoBox from './components/PlatformAdminRaffleInfoBox'
import { RaffleHostPromotionPage } from './components/RaffleHostPromotion'
import { Raid2EarnBox } from './components/Raid2EarnBox'
import { trpc } from '../../utils/trpc'
import { toast } from 'react-hot-toast'
import { CommunityBox } from './components/CommunityBox'
import AliceCarousel from 'react-alice-carousel'
import { RewardsRaffle } from '../rewards/components/RewardsRaffle'
import { useParticipantsForRaffle } from '../rewards/hooks/useParticipant'
import { format } from 'date-fns'
import { SignMessageToLoginButton } from './components/SignMessageToLoginButton'
import { NftImageGallery } from './components/NFTImage/NFTImageGallery'
import { NFTDetails } from './components/NFTDetailPage/NFTDetails'
import { NftProperties } from './components/NFTDetailPage/NFTProperties'
import { NFTNameInfoDetail } from './components/NFTDetailPage/NFTNameInfoDetail'
import _ from 'lodash'

type ICountdownProps = {
  hasEnded: boolean
  setRaffleEnded?: () => any
  raffle: raffleType
  onChainRaffleRes: UseOnChainRaffleType
}

const Lost = () => {
  const emojis = ['🥲', '🙃', '🥺', '🫠', '😶', '🥵', '🤷']
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'

  return (
    <Stack
      justify='space-between'
      alignItems='center'
      mt='1rem'
      bg='#FFD2C3'
      borderRadius='1.875rem'
      py={['1.5rem', '1.5rem', '2.5rem', '2.5rem']}
      px={['.5rem', '2.5rem', '2.5rem', '2.5rem']}
      direction='column'
      spacing='1rem'
    >
      <Box
        textAlign='center'
        borderRadius='1.875rem'
        bgGradient='linear(to-t, #A92D12, #F29B87)'
        color='white'
        w='full'
        py='3rem'
      >
        <Text fontSize='1.75rem'>{emojis[Math.floor(Math.random() * emojis.length)]}</Text>
        <Text fontWeight='600' fontSize='1rem'>
          No win this time.
        </Text>
      </Box>
      <NextLink href='/' passHref>
        <Link mx='auto' display='inline-block'>
          <Button variant={isDarkMode ? 'primaryDark' : 'primary'}>Check new Raffles</Button>
        </Link>
      </NextLink>
    </Stack>
  )
}

const DescriptionBox: React.FC<{ raffle: raffleType; hasEnded: boolean }> = ({ raffle, hasEnded }) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const wallet = useWallet()
  const [isEditable, setEditable] = useState(false)
  const [description, setDescription] = useState(raffle.description)
  const { isAuthed } = useUser()

  const updateRaffleDescriptionMut = trpc.useMutation('raffle.updateRaffleDescription', {
    onSuccess: () => {
      toast.success('Description updated')
    },
    onError: (e) => {
      toast.error(`Update failed: ${e.message}`)
    },
  })

  useEffect(() => {
    setDescription(raffle.description)
  }, [raffle.description])

  const isHost = wallet?.publicKey?.toBase58() === raffle.hostWallet

  if (!raffle.description && !isHost) return <></>

  return (
    <>
      <Box
        mt='1.5rem'
        bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
        borderRadius='1.875rem'
        py={['1.5rem', '1.5rem', '2.5rem', '2.5rem']}
        px={['.5rem', '2.5rem', '2.5rem', '2.5rem']}
        whiteSpace='pre-wrap'
      >
        <Text fontWeight='600' fontSize='1.25rem'>
          Description
        </Text>
        {!isEditable && (
          <>
            <Text whiteSpace={'pre-wrap'}>{raffle.description ?? 'No description yet.'}</Text>
            {isHost && !hasEnded && isAuthed && (
              <Button onClick={() => setEditable(true)} mt='1rem' variant='outline'>
                {!!raffle.description ? 'Edit description' : 'Add description'}
              </Button>
            )}
            {isHost && !hasEnded && !isAuthed && (
              <Stack justifyContent={'flex-start'} mt='2rem'>
                <Text textAlign='center'>
                  To change the description, please login first by signing a message with your wallet.
                </Text>
                <SignMessageToLoginButton />
              </Stack>
            )}
          </>
        )}
        {isEditable && isHost && (
          <>
            <Textarea
              value={description ?? undefined}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Raffling off this grail Stoned Ape with the Banana Jacket ranked 200'
            ></Textarea>

            <Button
              mt='1rem'
              variant='outline'
              onClick={async () => {
                await updateRaffleDescriptionMut.mutateAsync({
                  raffleId: raffle.id,
                  newDescription: description ?? '',
                })
                setEditable(false)
              }}
            >
              Save new description
            </Button>
          </>
        )}
      </Box>
    </>
  )
}

const ShippingTermsBox: React.FC<{ raffle: raffleType }> = ({ raffle }) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const wallet = useWallet()

  const isHost = wallet?.publicKey?.toBase58() === raffle.hostWallet

  if (raffle?.type !== 'IRL') return <></>

  return (
    <>
      <Box
        mt='1.5rem'
        bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
        borderRadius='1.875rem'
        py={['1.5rem', '1.5rem', '2.5rem', '2.5rem']}
        px={['.5rem', '2.5rem', '2.5rem', '2.5rem']}
        whiteSpace='pre-wrap'
      >
        <Text fontWeight='600' fontSize='1.25rem'>
          IRL Shipping Terms
        </Text>
        <Text whiteSpace={'pre-wrap'}>
          {raffle.irlShippingTerms ?? 'No shipping terms defined. Reach out to the raffle host for details.'}
        </Text>
      </Box>
    </>
  )
}

const Countdown: React.FC<ICountdownProps> = ({ hasEnded, raffle, onChainRaffleRes, setRaffleEnded }) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const [show, setShow] = useState<'COUNTDOWN' | 'DATE'>('COUNTDOWN')
  const isRaffleFinalized = useMemo(() => {
    return raffle.status === 'FINISHED' || raffle.status === 'CANCELLED'
  }, [raffle.status])

  const totalTicketsSold = useMemo(() => {
    return raffle.isRewardRaffle || isRaffleFinalized ? raffle.ticketsSoldFinal : onChainRaffleRes?.data?.ticketCount
  }, [raffle.isRewardRaffle, isRaffleFinalized, raffle.ticketsSoldFinal, onChainRaffleRes?.data?.ticketCount])

  return (
    <Stack
      justify='space-between'
      mt='1.5rem'
      bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
      borderRadius='1.875rem'
      py={['1.5rem', '1.5rem', '2.5rem', '2.5rem']}
      px={['.5rem', '2.5rem', '2.5rem', '2.5rem']}
      direction={['column', 'row', 'row', 'row']}
      spacing='1.5rem'
    >
      <Stack alignItems='center' direction={['column', 'row', 'row', 'row']}>
        {hasEnded || raffle.status === 'CANCELLED' ? (
          <HStack
            bg={isDarkMode ? 'transparent' : 'rgba(255, 255, 255, 1)'}
            spacing='0'
            rounded='full'
            border={isDarkMode ? '1px solid #494949' : '1px solid #E9E9E9'}
          >
            <Box px='1rem' py='.5rem' roundedLeft='full'>
              <Text fontWeight='600' fontSize={'1.15rem'}>
                {raffle.status === 'CANCELLED' ? 'Cancelled' : `Ended at ${format(raffle.ends, 'dd.MM.yyyy HH:mm')}`}
              </Text>
            </Box>
          </HStack>
        ) : (
          <>
            <RaffleCountdown
              raffleEnded={setRaffleEnded}
              variant='big'
              ends={raffle.ends}
              showStatusChange={(status) => setShow(status)}
            />
          </>
        )}

        <Text color='#BDBDBD' fontSize='0.875rem'>
          {show === 'COUNTDOWN' ? 'Time Left' : 'End Time'}
        </Text>
      </Stack>

      {!raffle.isRewardRaffle && (
        <Stack alignItems='center' direction={['column', 'row', 'row', 'row']}>
          {totalTicketsSold && raffle.maxTickets && totalTicketsSold >= raffle.maxTickets ? (
            <>
              <Text fontWeight='700' fontSize='1.25rem' color='#f13a3a'>
                Sold out 🔥
              </Text>
              <Text color='#BDBDBD' fontSize='0.875rem'>
                {!_.isNil(totalTicketsSold) ? totalTicketsSold.toString() + ' tickets sold' : <Spinner size='sm' mt='0.3rem' />}
              </Text>
            </>
          ) : (
            <>
              <Text fontWeight='600' fontSize='1.25rem'>
                {_.isNil(totalTicketsSold) ? (
                  <Box>
                    <Spinner size='sm' mt='0.3rem'></Spinner>
                  </Box>
                ) : (
                  (totalTicketsSold ?? 0)
                )}{' '}
                {raffle.maxTickets ? <>/ {raffle.maxTickets}</> : ''}
              </Text>
              <HStack>
                <Text mr='.4rem' color='#BDBDBD' fontSize='0.875rem'>
                  Tickets Sold
                </Text>
              </HStack>
            </>
          )}
        </Stack>
      )}
    </Stack>
  )
}

const RaffleParticipants: React.FC<{
  hasEnded: boolean
  raffle: raffleType
  raffleOnChainUserRes?: any
  onChainRaffleRes?: UseOnChainRaffleType
  winners?: PublicKey[]
}> = ({ raffle, onChainRaffleRes, hasEnded }) => {
  if (!raffle.raffleOnChainAddress) return <></>
  const { data: onChainRaffleParticipants, isLoading: participantsLoading } = useOnChainRaffleParticipants(
    new PublicKey(raffle.raffleOnChainAddress),
  )

  const participantsFromDBRes = useParticipantsForRaffle({
    raffleId: raffle.id,
    enabled: raffle.isRewardRaffle || raffle.status === 'FINISHED',
  })

  const sortedDBParticipants = useMemo(() => {
    console.log('participantsFromDBRes', participantsFromDBRes.data)

    if (participantsFromDBRes.data) {
      return participantsFromDBRes.data.sort((a, b) => b.ticketsBought - a.ticketsBought)
    }
    return []
  }, [participantsFromDBRes.data])
  console.log('after memo', sortedDBParticipants)

  const winners = useMemo(() => {
    if (participantsFromDBRes.data) {
      return raffle.winners.map((winner) => {
        const participant = (participantsFromDBRes.data ?? []).find((p) => p.userId === winner.id)

        return {
          ...winner,
          counter: participant?.ticketsBought,
        }
      })
    }

    const raffleWinners = raffle.winners.map((winner) => {
      const onChainParticipant = (onChainRaffleParticipants ?? []).find((p) => p.account.authority.toBase58() === winner.wallet)

      const dbParticipant = sortedDBParticipants.find((p) => {
        return p.user.wallet === winner.wallet
      })

      return {
        ...winner,
        counter:
          raffle.status === 'FINISHED' && dbParticipant ? dbParticipant.ticketsBought : onChainParticipant?.account.counter,
      }
    })

    return raffleWinners
  }, [raffle, onChainRaffleParticipants, participantsFromDBRes.data])

  const onChainWinnersCount = useMemo(() => {
    return onChainRaffleRes?.data?.winners.length ?? 0
  }, [onChainRaffleRes?.data?.winners.length])

  const shouldDisplayWinners = useMemo(() => {
    return onChainWinnersCount > 0 || raffle.status === 'FINISHED'
  }, [onChainWinnersCount, raffle.status])

  const sortedParticipants = useMemo(() => {
    return (onChainRaffleParticipants ?? []).sort((a, b) => b.account.counter - a.account.counter)
  }, [onChainRaffleParticipants])

  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'

  return (
    <Box
      mt='1.5rem'
      p={['0.5rem', '1.875rem', '2.875rem', '2.875rem']}
      borderRadius='1.875rem'
      border={isDarkMode ? '2px solid #494949' : '2px solid #E5E5E5'}
      maxHeight='39rem'
      overflowY='auto'
    >
      <HStack justify='space-between'>
        <Text fontWeight='600' fontSize='1.25rem'>
          Raffle Participants
        </Text>
        {raffle.status === 'FINISHED' ? (
          <HStack>
            <Flex gap='0.2rem'>
              {sortedDBParticipants
                ?.slice(0, 5)
                .map((p) => <RaffleParticipantAvatar key={p.user.wallet} wallet={p.user.wallet} />)}
            </Flex>
          </HStack>
        ) : (
          <HStack>
            <Flex gap='0.2rem'>
              {onChainRaffleParticipants
                ?.slice(0, 5)
                .map((u) => (
                  <RaffleParticipantAvatar key={u.account.authority.toBase58()} wallet={u.account.authority.toBase58()} />
                ))}
            </Flex>
            <Text color='#BDBDBD'>{onChainRaffleParticipants?.length ?? <Spinner size='sm' mt='0.3rem' />}</Text>
          </HStack>
        )}
      </HStack>

      {hasEnded && !shouldDisplayWinners && (
        <Text fontWeight={700} fontSize={'1.6rem'} align='left' mt='2rem'>
          Draw pending
        </Text>
      )}

      {shouldDisplayWinners && (
        <>
          <Text align='left' mt='2rem'>
            Winners
          </Text>
          {winners?.map((winner, i) => (
            <RaffleParticipant idx={i} isWinner={true} key={winner.wallet} wallet={winner.wallet} counter={winner.counter} />
          ))}
        </>
      )}
      {(sortedParticipants.length > 0 || sortedDBParticipants.length > 0) && (
        <Text align='left' mt='2rem'>
          Participants
        </Text>
      )}
      {!!sortedDBParticipants && sortedDBParticipants.length > 0 && raffle.status === 'FINISHED'
        ? sortedDBParticipants.map((p, i) => (
            <RaffleParticipant
              idx={i}
              isWinner={shouldDisplayWinners && winners?.some((w) => w.wallet === p.user.wallet)}
              key={p.user.wallet}
              wallet={p.user.wallet}
              user={p.user}
              counter={p.ticketsBought}
            />
          ))
        : sortedParticipants?.map((u, i) => (
            <RaffleParticipant
              idx={i}
              isWinner={shouldDisplayWinners && winners?.some((w) => w.wallet === u.account.authority.toBase58())}
              key={u.account.authority.toBase58()}
              wallet={u.account.authority.toBase58()}
              counter={u.account.counter}
            />
          ))}
      {!sortedDBParticipants &&
        (!sortedParticipants || sortedParticipants?.length < 1) &&
        !participantsLoading &&
        !participantsFromDBRes.isLoading && <Text>No participants so far.</Text>}
      {(participantsLoading || participantsFromDBRes.isLoading) && <Spinner size={'sm'}></Spinner>}
    </Box>
  )
}

const BenefitingCommunities: React.FC<{
  raffle: raffleType
}> = ({ raffle }) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  if (raffle.benefitingProjects?.length > 0) {
    return (
      <Box
        mt='1.5rem'
        bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
        borderRadius='1.875rem'
        py={['1.5rem', '1.5rem', '2.5rem', '2.5rem']}
        px={['.5rem', '2.5rem', '2.5rem', '2.5rem']}
      >
        <Text fontWeight='600' fontSize='1.25rem'>
          Benefiting Communities
        </Text>
        <Flex flexWrap='wrap' direction={['column', 'row', 'row', 'row']}>
          {raffle.benefitingProjects?.map((p) => <CommunityBox key={p.id} project={p}></CommunityBox>)}
          {!raffle &&
            [0, 1, 2].map((i) => (
              <Skeleton key={i} w={['100%', '13rem', '13rem', '13rem']} h='5rem' borderRadius='15px' mr='1rem' my='.5rem' />
            ))}
        </Flex>
      </Box>
    )
  } else {
    return <></>
  }
}

const TermsAndConditions: React.FC<{
  raffle: raffleType
}> = ({ raffle }) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  return (
    <Box
      mt='1.5rem'
      bg={isDarkMode ? 'cardBlack' : '#F9F9F9'}
      borderRadius='1.875rem'
      py={['1.5rem', '1.5rem', '2.5rem', '2.5rem']}
      px={['1rem', '2.5rem', '2.5rem', '2.5rem']}
    >
      <Text fontWeight='600' fontSize='1.25rem'>
        Terms and Conditions
      </Text>
      <Text fontSize={['.875rem', '1rem']} textAlign={'left'} whiteSpace={'pre-wrap'} lineHeight={'1.8rem'}>
        1. We put together a{' '}
        <Link
          href='https://www.notion.so/9340c49934f044738917c61d2982dd96'
          color={isDarkMode ? 'white' : 'black'}
          textDecoration='underline'
          target={'_blank'}
        >
          <b>guide for raffle buyers</b>
        </Link>{' '}
        for you to have the best possible user experience.
        <br></br>2.{' '}
        {raffle?.type === 'NFT'
          ? 'All NFT prizes are securely held in escrow by the raffle platform until the draw is conducted.'
          : `Distributing ${
              raffle.type === 'IRL' ? 'IRL items' : 'whitelist spots'
            } is responsibility of the creator, Monet has no liability for the delivery, please verify the creator of the raffle accordingly.`}
        <br></br>3. Raffle tickets are non-refundable once purchased, so make sure to consider your decision carefully.
        <br></br>4. If you don't win the raffle, your ticket cannot be refunded either.
        <br></br>5. Each participant can purchase a maximum of 20% of the total available tickets.
        <br></br>6. The host operates the raffle, while Monet provides the technical service to conduct it.
        {raffle?.type === 'IRL' && (
          <>
            <br></br>&nbsp;&nbsp;6a. For shipping terms, please refer to the information provided by the raffle host.
          </>
        )}
        <br></br>7. For more information, check out our full{' '}
        <Link
          href='https://www.notion.so/Monet-Terms-of-Use-87e446d1c8fb4d78a4285c30308db113'
          color={isDarkMode ? 'white' : 'black'}
          textDecoration='underline'
          target={'_blank'}
        >
          Terms of Use
        </Link>
        <br></br>8. More public information and an FAQ is available{' '}
        <Link
          href='https://www.notion.so/MONET-Public-Information-ec4e1c73b4d34227ae0782297ac50194'
          color={isDarkMode ? 'white' : 'black'}
          textDecoration='underline'
          target={'_blank'}
        >
          here
        </Link>
      </Text>
    </Box>
  )
}

interface IRaffleSingleProps {
  raffle: raffleTypeWithLikeCount
}

const DAppRaffleSingle: React.FC<IRaffleSingleProps> = ({ raffle: raffleInitialData }) => {
  const { data: raffle, ...raffleRes } = useRaffleBySlug(raffleInitialData)
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const tokens = useTokensQuick()
  const router = useRouter()
  const onChainRaffleRes = useOnChainRaffle(raffle?.id as string, true)
  const onChainUserRaffleRes = useOnChainRaffleUserData(onChainRaffleRes.data)
  const wallet = useWallet()
  const [possiblyEnded, setPossiblyEnded] = useState(false)
  const { data: isPlatformAdmin, isLoading: isPlatformAdminLoading } = useIsUserPlatformAdmin()

  const onChainWinnersCount = useMemo(() => {
    return onChainRaffleRes?.data?.winners.length ?? 0
  }, [onChainRaffleRes?.data?.winners.length])

  const shouldDisplayWinners = useMemo(() => {
    return raffle?.status === 'FINISHED'
  }, [raffle?.status])

  const userWon = useMemo(() => {
    return (
      !!wallet.publicKey &&
      raffle?.status === 'FINISHED' &&
      raffle?.winners.some((winner) => winner.wallet === wallet.publicKey?.toBase58())
    )
  }, [wallet, raffle])

  const creatorUserAddress = useMemo(() => {
    if (!raffle?.creatorUser) {
      return ''
    }
    if (raffle.creatorUser.name.length < 26) {
      return raffle.creatorUser.name
    }
    return raffle.creatorUser.name.slice(0, 8) + '..' + raffle.creatorUser.name.slice(-8)
  }, [raffle?.creatorUser])

  const hasEnded = useMemo(() => {
    return hasRaffleEnded(raffle)
  }, [raffle?.ends, possiblyEnded])

  if (!raffle) {
    return <></>
  }

  return (
    <Stack maxW='100%'>
      <HStack mb='1rem' mt='1rem' justify='space-between'>
        <Button
          variant={isDarkMode ? 'outlinedDark' : 'outlined'}
          fontSize={['.75rem', '1rem']}
          leftIcon={<BsArrowLeft size={10} />}
          onClick={() => navigateBack()}
        >
          Go back
        </Button>
        <ButtonGroup size='sm' isAttached variant='outline'>
          <IconButton
            onClick={() => window.open(`https://solscan.io/token/${raffle.nftMint}`)}
            rounded='full'
            aria-label='share'
            icon={<Image src='/icons/icon-so.png' />}
            _hover={{
              bg: '#f7f7f7',
            }}
          />
          <ShareModal link={`${getBaseUrl()}/r/${raffle.id}`} text={raffle.name} />
          <SaveRaffleButton raffleId={raffle?.id} likes={raffle._count.likedBy} />

          {!hasEnded && (
            <IconButton
              rounded='full'
              aria-label='flag'
              icon={<BsFlag />}
              _hover={{
                bg: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#f7f7f7',
              }}
              onClick={() =>
                window.open(
                  `https://docs.google.com/forms/d/e/1FAIpQLSf-Uyx4ISHelWZMLPCwAmc9lvCbh9G_kEsfcjOp8Nsr4XY7wA/viewform?usp=pp_url&entry.2007383009=${encodeURI(
                    `${getBaseUrl()}/r/${raffle.id}`,
                  )}`,
                )
              }
            />
          )}
        </ButtonGroup>
      </HStack>
      <Stack mt='10rem' textAlign='center' display={['flex', 'flex', 'none']}>
        <NftImageGallery imageUrl={raffle.imageUrl} additionalImages={raffle.additionalImageUrls} />
        <NFTNameInfoDetail isRaffle={true} raffle={raffle} onChainRaffleRes={onChainRaffleRes} />
        {shouldDisplayWinners && !!wallet.publicKey?.toBase58 && userWon && <Won raffle={raffle} />}
        {shouldDisplayWinners && !!wallet.publicKey?.toBase58 && !userWon && <Lost />}
        {raffle.hostWallet === wallet.publicKey?.toBase58() && (
          <RaffleHostInfoAndCancelBox
            raffle={raffle}
            onChainRaffleRes={onChainRaffleRes}
            onChainUserRaffleRes={onChainUserRaffleRes}
          ></RaffleHostInfoAndCancelBox>
        )}
        {!hasEnded && !raffle.isRewardRaffle && raffle.hostWallet !== wallet.publicKey?.toBase58() && (
          <BuyTickets raffle={raffle} onChainRaffleRes={onChainRaffleRes} onChainUserRaffleRes={onChainUserRaffleRes} />
        )}
        {isPlatformAdmin && (
          <PlatformAdminRaffleInfoBox raffle={raffle} onChainRaffleRes={onChainRaffleRes}></PlatformAdminRaffleInfoBox>
        )}
        {raffle.isRewardRaffle && <RewardsRaffle></RewardsRaffle>}

        <Tabs mt='3rem' variant='soft-rounded' colorScheme='gray' isLazy>
          <TabList paddingY='1rem' overflow='scroll' maxWidth='100%'>
            <Tab whiteSpace='nowrap' color={isDarkMode ? '#d8dce1' : 'black'}>
              Details
            </Tab>
            <Tab whiteSpace='nowrap' color={isDarkMode ? '#d8dce1' : 'black'}>
              Participants{' '}
            </Tab>
            <Tab whiteSpace='nowrap' color={isDarkMode ? '#d8dce1' : 'black'}>
              Buyer's Guide{' '}
            </Tab>
            {raffle.hostWallet === wallet.publicKey?.toBase58() && (
              <Tab whiteSpace='nowrap' color={isDarkMode ? '#d8dce1' : 'black'}>
                Host Info
              </Tab>
            )}
            {raffle.hostWallet === wallet.publicKey?.toBase58() && (
              <Tab whiteSpace='nowrap' color={isDarkMode ? '#d8dce1' : 'black'}>
                Promotion
              </Tab>
            )}
            {raffle.hostWallet === wallet.publicKey?.toBase58() && (
              <Tab whiteSpace='nowrap' color={isDarkMode ? '#d8dce1' : 'black'}>
                Raid
              </Tab>
            )}
            {isPlatformAdmin && (
              <Tab whiteSpace='nowrap' color={isDarkMode ? '#d8dce1' : 'black'}>
                Admin
              </Tab>
            )}
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              <Countdown hasEnded={hasEnded} raffle={raffle} onChainRaffleRes={onChainRaffleRes} />

              <DescriptionBox raffle={raffle} hasEnded={hasEnded}></DescriptionBox>

              <ShippingTermsBox raffle={raffle}></ShippingTermsBox>

              <BenefitingCommunities raffle={raffle} />

              <TermsAndConditions raffle={raffle}></TermsAndConditions>
            </TabPanel>
            <TabPanel p={0}>
              <RaffleParticipants
                hasEnded={hasEnded}
                raffle={raffle}
                onChainRaffleRes={onChainRaffleRes}
                raffleOnChainUserRes={onChainUserRaffleRes}
              />
            </TabPanel>

            <TabPanel p={0}>
              <TermsAndConditions raffle={raffle}></TermsAndConditions>
            </TabPanel>

            {raffle.hostWallet === wallet.publicKey?.toBase58() && (
              <TabPanel p={0}>
                <RaffleHostInfoAndCancelBox
                  raffle={raffle}
                  onChainRaffleRes={onChainRaffleRes}
                  onChainUserRaffleRes={onChainUserRaffleRes}
                ></RaffleHostInfoAndCancelBox>
              </TabPanel>
            )}

            {raffle.hostWallet === wallet.publicKey?.toBase58() && (
              <TabPanel p={0}>
                <RaffleHostPromotionPage raffle={raffle}></RaffleHostPromotionPage>
              </TabPanel>
            )}

            {raffle.hostWallet === wallet.publicKey?.toBase58() && (
              <TabPanel p={0}>
                <Raid2EarnBox raffle={raffle}></Raid2EarnBox>
              </TabPanel>
            )}

            {isPlatformAdmin && (
              <TabPanel p={0}>
                <PlatformAdminRaffleInfoBox raffle={raffle} onChainRaffleRes={onChainRaffleRes}></PlatformAdminRaffleInfoBox>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Stack>

      <Stack direction='row' display={['none', 'none', 'flex']} spacing='4rem'>
        <Box w='30rem'>
          <NftImageGallery imageUrl={raffle.imageUrl} additionalImages={raffle.additionalImageUrls} />
          {raffle.type === 'NFT' && <NftProperties attributes={raffle.attributes} />}
          <NFTDetails
            hostWallet={raffle.creatorUser?.wallet}
            isCreatorUser={!raffle.creatorProject}
            holdersOnly={raffle.holdersOnly}
            type={raffle.type}
            nftMint={raffle.nftMint ?? ''}
          />
        </Box>
        <Box ml='2.813rem' flex='12'>
          <NFTNameInfoDetail isRaffle={true} raffle={raffle} onChainRaffleRes={onChainRaffleRes} />
          {raffle.isRewardRaffle && <RewardsRaffle></RewardsRaffle>}

          <Box display='flex' flexDirection='column' gap='6'>
            <Countdown hasEnded={hasEnded} raffle={raffle} onChainRaffleRes={onChainRaffleRes} />
            {!hasEnded && !raffle.isRewardRaffle && (
              <BuyTickets raffle={raffle} onChainRaffleRes={onChainRaffleRes} onChainUserRaffleRes={onChainUserRaffleRes} />
            )}
          </Box>
          {shouldDisplayWinners && !!wallet.publicKey && userWon && <Won raffle={raffle} />}
          {shouldDisplayWinners && !!wallet.publicKey && !userWon && <Lost />}

          <Tabs mt='3rem' variant='soft-rounded' colorScheme='gray' isLazy>
            <TabList>
              <Tab color={isDarkMode ? '#d8dce1' : 'black'}>Details</Tab>
              <Tab color={isDarkMode ? '#d8dce1' : 'black'}>Participants </Tab>
              <Tab color={isDarkMode ? '#d8dce1' : 'black'}>Buyer's Guide </Tab>
              {raffle.hostWallet === wallet.publicKey?.toBase58() && (
                <Tab color={isDarkMode ? '#d8dce1' : 'black'}>Host Info</Tab>
              )}
              {raffle.hostWallet === wallet.publicKey?.toBase58() && (
                <Tab color={isDarkMode ? '#d8dce1' : 'black'}>Promotion</Tab>
              )}
              {raffle.hostWallet === wallet.publicKey?.toBase58() && <Tab color={isDarkMode ? '#d8dce1' : 'black'}>Raid</Tab>}
              {isPlatformAdmin && <Tab color={isDarkMode ? '#d8dce1' : 'black'}>Admin</Tab>}
            </TabList>
            <TabPanels>
              <TabPanel p={0}>
                <DescriptionBox raffle={raffle} hasEnded={hasEnded}></DescriptionBox>

                <ShippingTermsBox raffle={raffle}></ShippingTermsBox>
                {/* <RewardsRaffle /> */}
                <BenefitingCommunities raffle={raffle} />

                <TermsAndConditions raffle={raffle}></TermsAndConditions>
              </TabPanel>
              <TabPanel p={0}>
                <RaffleParticipants
                  hasEnded={hasEnded}
                  raffle={raffle}
                  onChainRaffleRes={onChainRaffleRes}
                  raffleOnChainUserRes={onChainUserRaffleRes}
                />
              </TabPanel>

              <TabPanel p={0}>
                <TermsAndConditions raffle={raffle}></TermsAndConditions>
              </TabPanel>

              {raffle.hostWallet === wallet.publicKey?.toBase58() && (
                <TabPanel p={0}>
                  <RaffleHostInfoAndCancelBox
                    raffle={raffle}
                    onChainRaffleRes={onChainRaffleRes}
                    onChainUserRaffleRes={onChainUserRaffleRes}
                  ></RaffleHostInfoAndCancelBox>
                </TabPanel>
              )}

              {raffle.hostWallet === wallet.publicKey?.toBase58() && (
                <TabPanel p={0}>
                  <RaffleHostPromotionPage raffle={raffle}></RaffleHostPromotionPage>
                </TabPanel>
              )}

              {raffle.hostWallet === wallet.publicKey?.toBase58() && (
                <TabPanel p={0}>
                  <Raid2EarnBox raffle={raffle}></Raid2EarnBox>
                </TabPanel>
              )}

              {isPlatformAdmin && (
                <TabPanel p={0}>
                  <PlatformAdminRaffleInfoBox raffle={raffle} onChainRaffleRes={onChainRaffleRes}></PlatformAdminRaffleInfoBox>
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </Box>
      </Stack>
    </Stack>
  )
}

export default DAppRaffleSingle
